import React from 'react';

export const BlogShopifySwatches = (props) => {
    return (
        <div className='blog-con'>
            <hr />
            <article>
                <p>If you're running a Shopify store and your product cards still show only text-based color names like “Red,” “Midnight Black,” or “Soft Beige,” you're missing one of the simplest and most effective UX upgrades: visual color swatches.</p>
                <p>Customers shop with their eyes first.Swatches remove friction, reduce clicks, and improve product discovery — all directly on the collection page.</p>
                <section>
                    <h2>Why You Should Add Color Swatches?</h2>
                    <p>Here are the practical advantages:</p>
                    <p><strong>Faster Product Browsing: </strong>Customers instantly see available color options without opening each product page. This reduces browsing time and makes navigation smoother.</p>
                    <p><strong>Higher Click-Through and Conversion Rates: </strong>Users often choose based on color preference. Showing options upfront increases engagement and the likelihood they’ll click into the exact variant they want.</p>
                    <p><strong>More Professional Store Appearance: </strong>Modern ecommerce sites use visual cues instead of forcing users to read variant names. Swatches immediately make your collection pages look more polished and trustworthy.</p>
                    <p><strong>Better Mobile Experience: </strong>Reading variant options on small screens is tedious. Tapping a color circle is not.</p>
                    <p><strong>Reduced Return Friction: </strong>When shoppers clearly see color differences, they make more accurate purchases — lowering confusion and unnecessary returns.</p>
                    <p>If you want your store to feel modern, intuitive, and visually appealing, swatches are one of the simplest upgrades you can make.</p>
                </section>
                <section>
                    <h3>Step 1: Create product with color variants</h3>
                    <p>Define color variants you want to use. Go to <b>Settings - Metafields and metaobjects </b>under <b>Metaobject definitions </b>add colors you want to add. i.e,</p>
                    <img src="/images/blogs/BlogSwatches3.png" alt="shopify-color-swatches-1" style={{ width: '100%', borderRadius: '20px', marginBottom: '20px' }} />
                    <p>Now Create a product and under the <b>Category metafield </b>section add colors you want to add.</p>
                    <img src="/images/blogs/BlogSwatches2.png" alt="shopify-color-swatches-1" style={{ width: '100%', borderRadius: '20px', marginBottom: '20px' }} />
                    <p>Now add <b>Color variants. </b>Define price, quntity and images.</p>
                    <img src="/images/blogs/BlogSwatches1.png" alt="shopify-color-swatches-1" style={{ width: '100%', borderRadius: '20px', marginBottom: '20px' }} />
                </section>
                <section>
                    <h3>Step 2: Drop This Code Into <code>card-product.liquid</code></h3>
                    <p><b>Add it right after the product image and before the <code>{`<div class="card__content">`}</code></b></p>
                    <p>(Yes, the location matters. No, Shopify will not correct your mistakes.)</p>
                    <pre><code>{`<div class="swatches">
  {% assign color_patterns = shop.metaobjects['shopify--color-pattern'].values %}  
  {% if card_product.variants.size > 1 %}
    {% assign color_option_index = '' %}
    {% for option in card_product.options_with_values %}
      {% if option.name == 'Color' %}
        {% assign color_option_index = forloop.index0 %} 
      {% endif %}
    {% endfor %}
    {% if color_option_index != '' %}
      {% assign colors = '' %}
      {% for variant in card_product.variants %}
        {% assign color = variant.options[color_option_index] | downcase %}
        {% unless colors contains color %}
          {% assign colors = colors | append: color | append: ',' %}
          {% assign selected_color = color %}
          {% for color in color_patterns %}
            {% assign color_label = color.label | downcase %}
            {% if color_label == selected_color %}
              {% assign bg_color = color.color %}
            {% endif %}
          {% endfor %}
          <a href="{{ variant.url }}">
            <div class="card__colors" style="background-color: {{ bg_color }};">
              {% comment %} Color {% endcomment %}
            </div>
          </a>
        {% endunless %}
      {% endfor %}
    {% else %}
      {%- comment -%} No Color option found — fallback to metafield {%- endcomment -%}
      {% for color_item in card_product.metafields.shopify.color-pattern.value %}
        <a href="{{ card_product.url }}">
          <div class="card__colors" style="background-color: {{ color_item.color }};">
            {% comment %} Color {% endcomment %}
          </div>
        </a>
      {% endfor %}
    {% endif %}

  {% else %}
    {%- comment -%} No variants — use metafield {%- endcomment -%}
    {% for color_item in card_product.metafields.shopify.color-pattern.value %}
      <a href="{{ card_product.url }}">
        <div class="card__colors" style="background-color: {{ color_item.color }};">
          {% comment %} Color {% endcomment %}
        </div>
      </a>
    {% endfor %}
  {% endif %}
</div>
`}</code></pre>
                    <p>✔ This code politely asks your theme:
                    <br /><i>“Hey buddy… which colors does this product actually come in?”</i>
                    <br />Then it paints little circles to show them.
                    <br /><br />It supports:</p>
                    <ul>
                        <li>Variant colors</li>
                        <li>Metafield fallback</li>
                        <li>That one chaotic product in your catalog with seven “blue-ish” shades</li>
                    </ul>
                </section>
                <section>
                    <h3>Step 3: Add the CSS (AKA: Give the Swatches a Soul)</h3>
                    <p>Now scroll to <code>base.css</code> (all the way down like a responsible coder) and paste this:</p>
                    <pre><code>{`.swatches {
  display: flex;
  gap: 10px;
  padding: 1.3rem 0rem 0rem 0rem;
  z-index: 2;
}

.card__colors {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 2px solid #ccc;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.card__colors:hover {
  transform: scale(1.1);
}
`}</code></pre>
                    <p>This CSS gives your color dots:</p>
                    <ul>
                        <li>Curves</li>
                        <li>Hover zoom</li>
                    </ul>
                    <p><b>Your product cards now look 200% more expensive.</b></p>
                </section>
                <section>
                    <h3>Step 4: Save. Refresh. Admire</h3>
                    <img src="/images/blogs/BlogSwatches5.png" alt="shopify-color-swatches-1" style={{ width: '100%', borderRadius: '20px', marginBottom: '20px' }} />
                    <p>Go ahead.
                    <br />Refresh your store.
                    <br />Stare at those swatches like a proud parent watching their child graduate from “meh” to “magnificent.”
                    <br />Your customers will now spend 37% more time clicking on little circles instead of thinking about their financial decisions.
                    <br /><b>This is the power of frontend beauty.</b></p>
                </section>
                <section>
                    <h3>Final Words of Wisdom</h3>
                    <p>If you can add color swatches, you can do anything.</p>
                    <ul>
                        <li>Want to add size buttons? Go for it.</li>
                        <li>Want to add swaches with images? Go for it.</li>
                        <li>Want to add a 3D dancing llama to each product card? Also possible, but maybe talk to a therapist first.</li>
                    </ul>
                    <p>Happy coding! &#128521;</p>
                </section>
                
            </article>
        </div>
    );
};
